import { css } from '@emotion/core';
import { Button, Menu, MenuItem, Theme, useTheme } from '@material-ui/core';
import { GetApp } from '@material-ui/icons';
import * as React from 'react';
import DocumentTitle from 'react-document-title';

import { buildAgentChartOption } from 'shared/common/charts/chart-option-builders';

import { echarts, ReactEChartsCore } from '../../common/charts/echarts-core';
import { useChartExport } from '../../hooks/use-chart-export';
import { AgentResultsHeader } from './agent-results-header';
import { ChartData } from './agent-types';
import { useChartContainerSize } from './charts/chart-sizing';

interface AgentResultsChartProps {
    chart: ChartData;
    className?: string;
}

const styles = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 25px 50px;
    flex: 1 1 auto;

    .chart-content {
        border: thin solid ${theme.palette.divider};
        border-radius: ${theme.shape.borderRadius}px;
        background: white;
        padding: 20px;
        resize: both;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        min-height: 300px;
        max-height: 1200px;
    }
`;

const useAgentChartOptions = (chart: ChartData) => {
    return React.useMemo(() => buildAgentChartOption(chart), [chart]);
};

export const AgentResultsChart: React.FC<AgentResultsChartProps> = ({ chart, className }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const chartExport = useChartExport({ title: chart.title });
    const containerRef = useChartContainerSize(chart, chartExport.chartRef);
    const option = useAgentChartOptions(chart);

    const handleDownloadMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleExportPng = () => {
        chartExport.exportAsPng();
        handleCloseMenu();
    };

    const handleExportSvg = () => {
        chartExport.exportAsSvg();
        handleCloseMenu();
    };

    const headerActions = (
        <>
            <Button startIcon={<GetApp />} onClick={handleDownloadMenuClick} style={{ color: 'white' }}>
                Download
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                <MenuItem onClick={handleExportPng}>PNG</MenuItem>
                <MenuItem onClick={handleExportSvg}>SVG</MenuItem>
            </Menu>
        </>
    );

    return (
        <DocumentTitle title={chart.title}>
            <div id="container">
                <AgentResultsHeader title={chart.title} actions={headerActions} />
                <div id="content" className="flex-fill">
                    <div className={className} css={styles(theme)}>
                        <div className="chart-content" ref={containerRef}>
                            <ReactEChartsCore
                                ref={chartExport.chartRef}
                                echarts={echarts}
                                option={option}
                                opts={{ renderer: 'svg' }}
                                style={{ height: '100%', width: '100%' }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </DocumentTitle>
    );
};
